import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { X, Mail, ArrowLeft, Loader2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useMagicLink } from '../hooks'
import { Logo } from '@/shared/components/logo'

type AuthStep = 'email' | 'check-inbox'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
}

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Unified authentication modal
 * Single flow: email → magic link
 * Supabase automatically creates account if email doesn't exist
 * Implementation: AUTH2-002
 */
export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<AuthStep>('email')
  const [email, setEmail] = useState('')
  const [inputError, setInputError] = useState<string | null>(null)
  const { sendMagicLink, isSending, error, clearError } = useMagicLink()

  const isValid = isValidEmail(email)
  const canSubmit = isValid && !isSending

  // Track previous isOpen state to detect open transition
  const prevIsOpenRef = useRef(false)

  // Reset state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened
      setStep('email')
      setEmail('')
      setInputError(null)
      clearError()
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, clearError])

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSending) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, isSending, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    if (!isSending) {
      onClose()
    }
  }, [isSending, onClose])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (inputError) setInputError(null)
    if (error) clearError()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    const success = await sendMagicLink(email)
    if (success) {
      setStep('check-inbox')
    }
  }

  const handleBack = () => {
    setStep('email')
    clearError()
  }

  const handleResend = async () => {
    await sendMagicLink(email)
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className={cn(
          'relative flex w-[440px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl',
          'animate-in fade-in zoom-in-95 duration-200'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isSending}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground disabled:opacity-50"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="px-8 pb-8 pt-10">
          {step === 'email' ? (
            <>
              {/* Logo */}
              <div className="mb-6 flex justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
                  <Logo className="h-7 w-7 text-white" />
                </div>
              </div>

              {/* Header */}
              <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-foreground">
                  {t('auth.getStarted')}
                </h2>
                <p className="mt-1 text-sm text-foreground-muted">
                  {t('auth.getStartedSubtitle')}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t('auth.emailPlaceholder')}
                    autoComplete="email"
                    autoFocus
                    disabled={isSending}
                    className={cn(
                      'w-full rounded-lg border bg-background px-4 py-3',
                      'text-foreground placeholder:text-foreground-subtle',
                      'focus:outline-none focus:ring-2',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      error || inputError
                        ? 'border-destructive focus:border-destructive focus:ring-destructive/20'
                        : 'border-border focus:border-accent focus:ring-accent/20'
                    )}
                  />
                  {(error || inputError) && (
                    <p className="mt-2 text-sm text-destructive">{error || inputError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={cn(
                    'flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3',
                    'font-medium text-white transition-colors hover:bg-accent-hover',
                    'focus:outline-none focus:ring-2 focus:ring-accent/20',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                >
                  {isSending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSending ? t('common.loading') : t('auth.continueWithEmail')}
                </button>
              </form>

              {/* Legal text with links */}
              <p className="mt-6 text-center text-xs text-foreground-subtle">
                {t('auth.termsAgreement')}{' '}
                <Link
                  to="/terms"
                  className="text-foreground-muted underline hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('auth.termsOfService')}
                </Link>{' '}
                {t('auth.and')}{' '}
                <Link
                  to="/privacy"
                  className="text-foreground-muted underline hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('auth.privacyPolicy')}
                </Link>
                .
              </p>
            </>
          ) : (
            /* Check Inbox Step */
            <>
              {/* Back button */}
              <button
                type="button"
                onClick={handleBack}
                className="mb-6 flex items-center gap-2 text-sm text-foreground-muted transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('common.back')}
              </button>

              {/* Icon */}
              <div className="mb-6 flex justify-center">
                <div className="rounded-full bg-accent/10 p-4">
                  <Mail className="h-8 w-8 text-accent" />
                </div>
              </div>

              {/* Header */}
              <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-foreground">
                  {t('auth.checkInbox')}
                </h2>
                <p className="mt-2 text-sm text-foreground-muted">
                  {t('auth.linkSentTo')}{' '}
                  <span className="font-medium text-foreground">{email}</span>.{' '}
                  {t('auth.clickToAccess')}
                </p>
              </div>

              {/* Instructions */}
              <div className="mb-6 rounded-lg bg-background-secondary p-4">
                <p className="text-sm text-foreground-muted">
                  {t('auth.linkExpires')}{' '}
                  {t('auth.didntReceive')}{' '}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isSending}
                    className="font-medium text-accent hover:underline disabled:opacity-50"
                  >
                    {t('auth.tryAgain')}
                  </button>
                  .
                </p>
              </div>

              {/* Resend button */}
              <button
                type="button"
                onClick={handleResend}
                disabled={isSending}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 py-3',
                  'font-medium text-foreground transition-colors hover:bg-background-secondary',
                  'focus:outline-none focus:ring-2 focus:ring-accent/20',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSending ? t('common.loading') : t('auth.resendLink')}
              </button>

              {/* Help text */}
              <p className="mt-6 text-center text-xs text-foreground-subtle">
                {t('auth.checkSpam')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
